import { useEffect, useRef, useState } from "react";
import type { Video } from "@/types/video";
import { formatDuration } from "@/utils/video";

interface VideoPlayerProps {
  video: Video;
}

export default function VideoPlayer({ video }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setMuted(false);
  }, [video.id]);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setProgress((prev) => Math.min(video.duration, prev + 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, video.duration]);

  useEffect(() => {
    if (progress >= video.duration && video.duration > 0) {
      setPlaying(false);
    }
  }, [progress, video.duration]);

  const togglePlay = () => {
    if (progress >= video.duration) setProgress(0);
    setPlaying((prev) => !prev);
  };

  const handleSeek = (value: number) => {
    setProgress(value);
  };

  const toggleFullscreen = async () => {
    const node = containerRef.current;
    if (!node) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await node.requestFullscreen();
      }
    } catch {
      // fullscreen not available in this browser context
    }
  };

  const percent = video.duration > 0 ? (progress / video.duration) * 100 : 0;

  return (
    <div>
      <div
        ref={containerRef}
        className="group relative aspect-video w-full overflow-hidden rounded-lg border border-background-800 bg-background-900"
      >
        <img
          src={video.cover}
          alt={`${video.title} 播放画面示意`}
          title={`${video.title} - 亚洲激情视频播放演示`}
          className={`h-full w-full object-cover object-top transition-all duration-700 ${
            playing ? "scale-105 opacity-90" : "opacity-100"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/10 to-background-950/30" />

        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="flex items-center gap-1 rounded bg-background-950/70 px-2 py-1 text-[11px] text-accent-300 backdrop-blur-sm">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-record-circle-line text-[11px]" />
            </span>
            播放演示 · 无真实视频流
          </span>
        </div>

        {!playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="开始播放演示"
            className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-primary-300/50 bg-primary-600/90 backdrop-blur-sm transition-transform duration-300 group-hover:scale-105 md:h-20 md:w-20">
              <i className="ri-play-fill text-3xl text-background-50 md:text-4xl" />
            </span>
            <span className="text-xs text-foreground-200">
              {progress > 0 ? "继续播放演示" : "点击播放演示片段"}
            </span>
          </button>
        )}

        {playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="暂停演示"
            className="absolute inset-0 flex cursor-pointer items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-950/70 backdrop-blur-sm">
              <i className="ri-pause-fill text-3xl text-foreground-50" />
            </span>
          </button>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background-950 via-background-950/80 to-transparent px-4 pb-3 pt-8">
          <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-foreground-700/50">
            <div
              className="h-full rounded-full bg-primary-500 transition-[width] duration-300"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? "暂停" : "播放"}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-100 transition-colors hover:bg-background-800/80"
              >
                <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-lg`} />
              </button>
              <button
                type="button"
                onClick={() => setMuted((prev) => !prev)}
                aria-label={muted ? "取消静音" : "静音"}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-100 transition-colors hover:bg-background-800/80"
              >
                <i className={`${muted ? "ri-volume-mute-line" : "ri-volume-up-line"} text-base`} />
              </button>
              <span className="text-[11px] tabular-nums text-foreground-300">
                {formatDuration(progress)} / {formatDuration(video.duration)}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <input
                type="range"
                min={0}
                max={video.duration}
                value={progress}
                onChange={(event) => handleSeek(Number(event.target.value))}
                aria-label="播放进度"
                className="hidden w-32 cursor-pointer accent-primary-500 md:block"
              />
              <button
                type="button"
                onClick={toggleFullscreen}
                aria-label="全屏"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-100 transition-colors hover:bg-background-800/80"
              >
                <i className="ri-fullscreen-line text-base" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 flex items-start gap-2 rounded-md border border-background-800 bg-background-900 px-3 py-2 text-[11px] leading-relaxed text-foreground-500">
        <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <i className="ri-information-line text-primary-400" />
        </span>
        本播放器为界面演示，仅模拟播放、暂停与进度控制，站内不提供真实视频流服务，也不承诺任何可播放内容。
      </p>
    </div>
  );
}